/**
 * react-query wiring for `/api/practice/*` (brief §practice). Query keys are
 * namespaced `["practice", …]` per `docs/CONVENTIONS.md`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@client/api/client";
import { useTopicsAndChapters } from "@client/api/topics";
import type {
  AnswerBody,
  AnswerResponse,
  CheckPartBody,
  CheckPartResponse,
  HintResponse,
  HistoryResponse,
  PracticeQuestionsResponse,
  RelatedResponse,
  RevealBody,
  RevealResponse,
  SelfExplanationBody,
  SelfExplanationResponse,
  StartPracticeParams,
} from "./types";

export const practiceKeys = {
  all: ["practice"] as const,
  history: (limit: number) => ["practice", "history", limit] as const,
};

function questionsQueryString(params: StartPracticeParams): string {
  if ("lecture" in params)
    return new URLSearchParams({ course: params.course, lecture: params.lecture }).toString();
  const search = new URLSearchParams();
  search.set("mode", params.mode);
  if (params.chapters && params.chapters.length > 0)
    search.set("chapters", params.chapters.join(","));
  if (params.topics && params.topics.length > 0) {
    search.set("topics", params.topics.join(","));
    if (params.primaryOnly) search.set("primaryOnly", "true");
  }
  if (params.types && params.types.length > 0) search.set("types", params.types.join(","));
  if (params.difficulty && params.difficulty.length > 0)
    search.set("difficulty", params.difficulty.join(","));
  if (params.taught && params.mode === "mixed") search.set("taught", params.taught);
  search.set("limit", String(params.limit));
  return search.toString();
}

/** Fetches one batch of practice questions — invoked from the "Starta" button. */
export function useStartPractice() {
  return useMutation({
    mutationFn: (params: StartPracticeParams) =>
      api.get<PracticeQuestionsResponse>(`/api/practice/questions?${questionsQueryString(params)}`),
  });
}

/**
 * Chapters + topics for the setup form. Re-exported from `@client/api/topics`
 * so practice, flashcards, progress and the home page share one cache entry.
 */
export { useTopicsAndChapters };

/**
 * A graded answer feeds "Senaste övningar" (`RecentPractice.tsx`, `usePracticeHistory`,
 * 30 s staleTime): without invalidating on success, pressing "Ny övning" right after
 * finishing a question could still show the stale list (bug: stale history).
 */
export function useAnswerQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AnswerBody) => api.post<AnswerResponse>("/api/practice/answer", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: practiceKeys.all }),
  });
}

/**
 * One numeric part graded on its own (B4, pedagogy spec §4.3.4): right or wrong and the near-miss
 * kind, never a value. It writes nothing on the server, so unlike `useAnswerQuestion` it
 * invalidates nothing.
 */
export function useCheckPart() {
  return useMutation({
    mutationFn: (body: CheckPartBody) =>
      api.post<CheckPartResponse>("/api/practice/check-part", body),
  });
}

/** A reveal ("give up") also lands an attempt row, so it must invalidate too — see above. */
export function useRevealQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RevealBody) => api.post<RevealResponse>("/api/practice/reveal", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: practiceKeys.all }),
  });
}

/**
 * The student's own sentence about a try (C-3). A plain function rather than a mutation: the
 * field also posts as it unmounts ("Nästa"), when no component is left to observe the request.
 */
export function postSelfExplanation(body: SelfExplanationBody) {
  return api.post<SelfExplanationResponse>("/api/practice/self-explanation", body);
}

export function useHint() {
  return useMutation({
    mutationFn: ({ questionId, index }: { questionId: string; index: number }) =>
      api.get<HintResponse>(
        `/api/practice/hint?questionId=${encodeURIComponent(questionId)}&index=${index}`,
      ),
  });
}

export function usePracticeHistory(limit = 50) {
  return useQuery({
    queryKey: practiceKeys.history(limit),
    queryFn: () => api.get<HistoryResponse>(`/api/practice/history?limit=${limit}`),
  });
}

export function relatedQueryString(
  questionIds: readonly string[],
  exclude: readonly string[],
): string {
  const search = new URLSearchParams();
  search.set("questionIds", questionIds.join(","));
  if (exclude.length > 0) search.set("exclude", exclude.join(","));
  return search.toString();
}

/**
 * Fresh related problems (learning-evidence decision #10), one per id in `questionIds`;
 * `exclude` is every id already in the session.
 */
export function useRelatedQuestions() {
  return useMutation({
    mutationFn: ({ questionIds, exclude }: { questionIds: string[]; exclude: string[] }) =>
      api.get<RelatedResponse>(`/api/practice/related?${relatedQueryString(questionIds, exclude)}`),
  });
}
